import React, { useCallback, useMemo, useState } from 'react';
import { CustomViz } from 'apps/sigma2-ui/src/components/custom-viz/CustomViz';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { SummaryStatsComponentProps } from 'libs/end-to-end/src/summary-stats';
import { SummaryStatsCustomVizDefinition } from 'libs/end-to-end/src/summary-stats-definition';
import { SummaryStatsOutput } from 'libs/end-to-end/src/summary-stats';
import { SummaryStatsRequest } from 'libs/end-to-end/src/summary-stats';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useOnUserAnalysisCancelCallback } from 'apps/sigma2-ui/src/lib/hooks/useOnUserAnalysisCancelCallback';
import { getFiltersFromSecondaryFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getListValueFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSecondaryFilterSelectionsFromCustomVizFormat } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { OnApplyFnArgs } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { useFilters } from 'apps/sigma2-ui/src/filters-v2/lib/useFilters';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { GetCustomVizRequestArgs } from 'apps/sigma2-ui/src/filters-v2/components/SaveAnalysisButton';
import { useShareLinkRequest } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useShareLinkSearchParameter } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { HorizontalFilterBar } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { parseCustomVizResult } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { ExplorePageFailure } from 'apps/sigma2-ui/src/explore-section/components/ExplorePageFailure';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { safeParseFloat } from 'libs/end-to-end/src/math';
import { formatNumberForDisplayInUnits } from 'libs/end-to-end/src/number-formatting';
import { determineUnitOfMetric } from 'libs/end-to-end/src/metrics';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useTouchableTooltipCollection } from 'apps/sigma2-ui/src/lib/hooks/useTouchableTooltipCollection';
import { TableAndSummaryTooltip } from 'apps/sigma2-ui/src/explore-section/components/TableAndSummaryTooltip';
import { DataRefreshIndicator } from 'apps/sigma2-ui/src/components/custom-viz/DataRefreshIndicator';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';
import { ViewConfigurationSchema } from 'libs/end-to-end/src/view-configurations';
import { OverallDisplayModeFilter } from 'apps/sigma2-ui/src/components/OverallDisplayModeFilter';

interface ExtendedSummaryStatsComponentProps
  extends SummaryStatsComponentProps {
  setViewConfiguration?: (viewConfiguration: ViewConfigurationSchema) => void;
}

export const SummaryStatsComponentPure = (
  props: ExtendedSummaryStatsComponentProps,
) => {
  const { data, viewConfiguration, setViewConfiguration } = props;
  const language = useLanguage();
  const { myObservation } = useObservation();

  const getMetricFromColumnName = useCallback(
    (
      columnName: string,
    ): {
      focusArea: FocusArea;
      metric: MyMetricDefinition;
    } | null => {
      if (!myObservation) {
        return null;
      }

      // check if column name is a metric
      for (const fa of myObservation.focusAreas) {
        const metric = fa.metrics.find(
          (m) => m.columnHeaderValue === columnName,
        );

        if (metric) {
          return {
            focusArea: fa.focusArea,
            metric,
          };
        }
      }

      return null;
    },
    [myObservation],
  );

  const getColumnDisplayName = useCallback(
    (columnName: string): string => {
      if (!myObservation) {
        return columnName;
      }

      // check if column name is a metric
      const metricInfo = getMetricFromColumnName(columnName);

      if (metricInfo) {
        return language.metricDisplayName({
          definition: metricInfo.metric,
        });
      }

      // if not found, return the column name as is
      return columnName;
    },
    [getMetricFromColumnName, language, myObservation],
  );

  const tooltipItems = useMemo(() => {
    return data.columnHeaders.map((columnHeader, columnIndex) => ({
      key: columnHeader,
      title: getColumnDisplayName(columnHeader),
      sampleSize: data.sampleSizes[columnIndex] ?? 0,
      value: data.values[columnIndex] ?? '',
    }));
  }, [data.columnHeaders, data.sampleSizes, data.values, getColumnDisplayName]);

  const { createTouchTarget, renderTooltip } = useTouchableTooltipCollection({
    items: tooltipItems,
    tooltipFn: (item) => {
      const averageDisplay = formatCellValue({
        rawValue: item.value,
        header: item.key,
      });

      const sampleSizeDisplay = item.sampleSize.toFixed(0);

      const numeratorDisplay = data.booleanColumns.includes(item.key)
        ? (data.numerators[
            data.columnHeaders.findIndex((ch) => ch === item.key)
          ]?.toFixed(0) ?? '')
        : '';

      return (
        <TableAndSummaryTooltip
          title={item.title}
          valueDisplay={averageDisplay}
          numeratorDisplay={numeratorDisplay}
          sampleSizeDisplay={sampleSizeDisplay}
        />
      );
    },
  });

  const formatCellValue = useCallback(
    // eslint-disable-next-line complexity
    ({ rawValue, header }: { rawValue: string; header: string }) => {
      const metricInfo = getMetricFromColumnName(header);
      const noValue =
        rawValue === undefined || rawValue === null || rawValue === '';
      const valueParsed = rawValue ? safeParseFloat(rawValue) : null;

      if (!metricInfo) {
        if (noValue || valueParsed === null) {
          return '';
        }

        return formatNumberForDisplayInUnits({
          units: 'count',
          isPercentageAlreadyMultipliedBy100: false,
          value: valueParsed,
        });
      }

      if (metricInfo) {
        if (noValue || valueParsed === null) {
          return '';
        }

        const units = determineUnitOfMetric({
          knownMaximum: null,
          isMetricBoolean: metricInfo.metric.isMetricBoolean,
          metricFocusArea: metricInfo.focusArea,
          definitionUnit: metricInfo.metric.unit,
        });

        return formatNumberForDisplayInUnits({
          units,
          isPercentageAlreadyMultipliedBy100: false,
          value: valueParsed,
        });
      }

      // if we reach here, it means we have a string value or no value
      return rawValue;
    },
    [getMetricFromColumnName],
  );

  if (!myObservation) {
    return <></>;
  }

  return (
    <div className="p-8">
      <Spacing direction="vertical" size="md">
        <OverallDisplayModeFilter
          viewConfiguration={viewConfiguration}
          setViewConfiguration={setViewConfiguration}
        />
        <div className="flex flex-row flex-wrap gap-14 relative">
          {data.columnHeaders.map((columnHeader, columnIndex) => {
            let topLineText = '';
            let bottomLineText = '';

            const averageDisplay = formatCellValue({
              rawValue: data.values[columnIndex] ?? '',
              header: columnHeader,
            });

            const numeratorAsString =
              data.numerators[columnIndex]?.toString() ?? '';

            const totalSample = String(data.sampleSizes[columnIndex] ?? 0);

            const whiteLabelingSubjectPlural =
              getMetricFromColumnName(columnHeader)?.metric.whiteLabeling
                ?.subjectPlural ?? null;

            if (viewConfiguration.overallDisplayMode === 'count') {
              topLineText = data.booleanColumns.includes(columnHeader)
                ? numeratorAsString
                : 'N/A';
              bottomLineText = data.booleanColumns.includes(columnHeader)
                ? language.subjectsCount({
                    subjectsCount: averageDisplay,
                    totalSample,
                    whiteLabelingSubjectPlural,
                  })
                : averageDisplay;
            } else {
              topLineText = averageDisplay;

              bottomLineText = data.booleanColumns.includes(columnHeader)
                ? language.subjectsCount({
                    subjectsCount: numeratorAsString,
                    totalSample,
                    whiteLabelingSubjectPlural,
                  })
                : '';
            }

            return (
              <div key={columnHeader} className="flex flex-col">
                <S2Typography
                  variant={'body-6'}
                  textColorVariant="gray-600"
                  translatedContent={getColumnDisplayName(columnHeader)}
                />
                {createTouchTarget({
                  key: columnHeader,
                  children: (
                    <>
                      {topLineText && (
                        <S2Typography
                          variant={'body-2-extralight'}
                          as="span"
                          textColorVariant="gray-900"
                          translatedContent={topLineText}
                        />
                      )}
                      {bottomLineText && (
                        <S2Typography
                          variant={'body-6-bold'}
                          textColorVariant="gray-600"
                          translatedContent={bottomLineText}
                        />
                      )}
                    </>
                  ),
                })}
              </div>
            );
          })}
          {renderTooltip()}
        </div>
      </Spacing>
    </div>
  );
};

export const SummaryStatsStatefulComponent = () => {
  const { myObservation, selectedOrganizationId } = useObservation();

  const { myUser, userAuthenticatedApiCall } = useUser();

  const context = useCustomViz();
  const { doViz } = context;

  const [viewConfiguration, setViewConfiguration] =
    useState<ViewConfigurationSchema>({});

  const shareLinkRawData = useShareLinkSearchParameter();

  const getCustomVizRequest = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      GetCustomVizRequestArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >): {
      customVizRequest: SummaryStatsRequest | undefined;
      viewConfiguration?: ViewConfigurationSchema;
    } => {
      if (!myObservation || !selectedOrganizationId) {
        return { customVizRequest: undefined };
      }

      const metrics =
        getListValueFromPrimaryFilterValues({
          primaryFilterKey: 'metrics',
          primaryFilterValues: primaryFilterSelections,
        }) ?? [];

      const filters = getFiltersFromSecondaryFilterSelections({
        secondaryFilterSelections,
      });

      const customVizRequest: SummaryStatsRequest = {
        customVizId: 'summaryStats',
        inputs: {
          extras: {
            metrics,
            filters,
          },
          dropdowns: {},
        },
        observationLocationType: myObservation.type,
        organizationId: selectedOrganizationId,
      };

      return {
        customVizRequest,
        viewConfiguration,
      };
    },
    [myObservation, selectedOrganizationId, viewConfiguration],
  );

  const onAnalyzeWithParams = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      OnApplyFnArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >) => {
      const { customVizRequest } = getCustomVizRequest({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      if (
        !customVizRequest ||
        customVizRequest.customVizId !== 'summaryStats'
      ) {
        return;
      }

      doViz(customVizRequest);

      setViewConfiguration((oldConfig) => ({
        ...oldConfig,
        overallDisplayMode: undefined,
      }));
    },
    [doViz, getCustomVizRequest],
  );

  const onApplySummaryStats = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
      isProgrammaticChange,
      isInitialApply,
    }: OnApplyFnArgs) => {
      if (
        isProgrammaticChange ||
        (isInitialApply && shareLinkRawData !== null)
      ) {
        return;
      }

      onAnalyzeWithParams({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [onAnalyzeWithParams, shareLinkRawData],
  );

  const filterPayload = useFilters({
    feature: 'summaryStats',
    focusArea: null,
    disabled: false,
    metricDefinitionWhiteLabeling: null,
    onApply: onApplySummaryStats,
    getCustomVizRequest,
    analysisName: undefined,
  });

  const onShareLinkLoaded = useCallback(
    (request: CustomVizRequest | null) => {
      if (
        !myObservation ||
        !request ||
        request.customVizId !== 'summaryStats'
      ) {
        return;
      }

      const filters = request.inputs.extras.filters;

      const secondaryFilterSelections =
        getSecondaryFilterSelectionsFromCustomVizFormat({
          feature: 'summaryStats',
          focusArea: null,
          customVizFilters: filters,
          focusAreas: myObservation.focusAreas,
          filterSetDefinitionsWithValues:
            myObservation.filterSetDefinitionsWithValues,
          dimensionDefinitions: myObservation.dimensions,
          analysisName: undefined,
          pipelineAnalysisConfiguration:
            myObservation.pipelineAnalysisConfiguration,
        });

      const primaryFilterSelections = getSummaryStatsPrimaryFilterSelections({
        request,
      });

      filterPayload.setFilters({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      onAnalyzeWithParams({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [filterPayload, myObservation, onAnalyzeWithParams],
  );

  useShareLinkRequest({
    onLoaded: onShareLinkLoaded,
    myUser,
    canLoad: filterPayload.isInitialized,
    userAuthenticatedApiCall,
    type: SummaryStatsRequest,
  });

  const onUserAnalysisCancel = useOnUserAnalysisCancelCallback({
    onShareLinkLoaded,
    resetFiltersToInitialState: filterPayload.resetFiltersToInitialState,
    resetMetricsToInitialState: null,
    resetDimensionsToInitialState: null,
  });

  const { customVizRequest: lastCustomVizRequest } = useMemo(
    () =>
      getCustomVizRequest({
        primaryFilterSelections: filterPayload.primaryFilterSelections,
        secondaryFilterSelections: filterPayload.secondaryFilterSelections,
      }),
    [
      filterPayload.primaryFilterSelections,
      filterPayload.secondaryFilterSelections,
      getCustomVizRequest,
    ],
  );

  return (
    <div>
      <UserAnalysesOverlay onUserAnalysisCancel={onUserAnalysisCancel} />
      <Spacing direction="vertical" size="md" className="min-h-96">
        <HorizontalFilterBar
          items={filterPayload.items}
          slotRight={
            <DataRefreshIndicator
              status={context.status}
              resultCreatedAt={context.resultCreatedAt}
            />
          }
        />

        <SummaryStatsCard
          customVizRequest={lastCustomVizRequest}
          result={context.result}
          status={context.status}
          viewConfiguration={viewConfiguration}
          setViewConfiguration={setViewConfiguration}
        />
      </Spacing>
    </div>
  );
};

export const SummaryStatsFullComponent = () => {
  const { myUser, userAuthenticatedApiCall } = useUser();
  const { myObservation } = useObservation();

  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();

  if (
    !myObservation ||
    !selectedDatabaseRevisionId ||
    !selectedDatabaseId ||
    !selectedOrganizationId ||
    !myUser
  ) {
    return <></>;
  }

  return (
    <CustomViz
      observation={myObservation.type}
      organizationId={selectedOrganizationId}
      customVizDefinition={SummaryStatsCustomVizDefinition}
      userAuthenticatedApiCall={userAuthenticatedApiCall}
    >
      <SummaryStatsStatefulComponent />
    </CustomViz>
  );
};

export const SummaryStatsCard = ({
  result,
  status,
  viewConfiguration,
  setViewConfiguration,
}: {
  customVizRequest: SummaryStatsRequest | undefined;
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
  setViewConfiguration?: (viewConfigurations: ViewConfigurationSchema) => void;
  viewConfiguration: ViewConfigurationSchema;
}) => {
  const { isLoading, data, hasFailed } = useMemo(
    () =>
      parseCustomVizResult({
        result,
        status,
        ResultZodType: SummaryStatsOutput,
      }),
    [result, status],
  );

  if (hasFailed && !isLoading) {
    return <ExplorePageFailure />;
  }

  if (isLoading || !data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        w="full"
        h="full"
        flex="1"
      >
        <CustomVizCircularSpinner />
      </Box>
    );
  }

  return (
    <SummaryStatsComponentPure
      data={data}
      isLoading={isLoading}
      setViewConfiguration={setViewConfiguration}
      viewConfiguration={viewConfiguration}
    />
  );
};

export const getSummaryStatsPrimaryFilterSelections = ({
  request,
}: {
  request: Pick<SummaryStatsRequest, 'inputs'>;
}): PrimaryFilterSelection[] => {
  return [
    {
      primaryFilterKey: 'metrics',
      value: request.inputs.extras.metrics ?? [],
    },
  ];
};
